import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";

const MySwal = withReactContent(Swal);

// Modal Component for Add/Edit Country
const CountryModal = ({ isOpen, onClose, onSubmit, initialData, isEdit }) => {
    const [formData, setFormData] = useState(
        initialData || { name_en: "", name_ar: "", image: null }
    );
    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(
        isEdit && initialData?.image
            ? `https://ligasos-laravel.ligasos.co/storage/app/public/${initialData.image}`
            : null
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "image" && files[0]) {
            setFormData({ ...formData, image: files[0] });
            setImagePreview(URL.createObjectURL(files[0]));
        } else {
            setFormData({ ...formData, [name]: value });
        }
        setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name_en) newErrors.name_en = "English name is required";
        if (!formData.name_ar) newErrors.name_ar = "Arabic name is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setIsSubmitting(true);
            const data = new FormData();
            data.append("name_en", formData.name_en);
            data.append("name_ar", formData.name_ar);
            if (formData.image) data.append("image", formData.image);
            await onSubmit(data);
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[300]">
            <div className="bg-[#0B2C3D] p-8 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-2xl font-extrabold text-white">{isEdit ? "Edit Country" : "Add Country"}</h3>
                    <button onClick={onClose} className="text-white hover:text-[#B39359] transition-colors duration-200">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={handleFormSubmit} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-white">English Name</label>
                        <input
                            type="text"
                            name="name_en"
                            value={formData.name_en}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter English name"
                        />
                        {errors.name_en && <p className="text-red-400 text-sm mt-1">{errors.name_en}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Arabic Name</label>
                        <input
                            type="text"
                            name="name_ar"
                            value={formData.name_ar}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white focus:border-[#B39359] focus:ring-2 focus:ring-[#B39359] outline-none transition-colors duration-300 placeholder-gray-400 disabled:opacity-50"
                            placeholder="Enter Arabic name"
                        />
                        {errors.name_ar && <p className="text-red-400 text-sm mt-1">{errors.name_ar}</p>}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white">Image (optional)</label>
                        <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="mt-1 w-full p-3 rounded-lg border border-[#1A4A5B] bg-[#0B2C3D] text-white file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#B39359] file:text-white hover:file:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50"
                        />
                        {imagePreview && (
                            <img
                                src={imagePreview}
                                alt="Preview"
                                className="mt-3 w-20 h-20 rounded-full object-cover border-2 border-[#B39359]"
                            />
                        )}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="py-2 px-5 rounded-lg bg-[#B39359] text-white hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                                    Processing...
                                </>
                            ) : (
                                <>{isEdit ? "Update" : "Add"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default function Countries() {
    const [countries, setCountries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editCountry, setEditCountry] = useState(null);
    const token = localStorage.getItem("token");

    // Fetch all countries
    useEffect(() => {
        const fetchCountries = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(
                    "https://ligasos-laravel.ligasos.co/api/countries",
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setCountries(response.data);
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch countries. Please try again.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchCountries();
    }, [token]);

    // Handle Add Country
    const handleAddCountry = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                "https://ligasos-laravel.ligasos.co/api/countries",
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setCountries([...countries, response.data.data]);
            setIsAddModalOpen(false);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Country added successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to add country.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Edit Country
    const handleEditCountry = async (data) => {
        setIsLoading(true);
        try {
            const response = await axios.post(
                `https://ligasos-laravel.ligasos.co/api/countries/${editCountry.id}?_method=PUT`,
                data,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );
            setCountries(countries.map((c) => (c.id === editCountry.id ? response.data.data : c)));
            setIsEditModalOpen(false);
            setEditCountry(null);
            MySwal.fire({
                icon: "success",
                title: "Success",
                text: "Country updated successfully!",
                confirmButtonColor: "#B39359",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (err) {
            MySwal.fire({
                icon: "error",
                title: "Error",
                text: err.response?.data?.message || "Failed to update country.",
                confirmButtonColor: "#B39359",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle Delete Country
    const handleDeleteCountry = async (id) => {
        const result = await MySwal.fire({
            title: "Are you sure?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#B39359",
            cancelButtonColor: "#dc2626",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axios.delete(
                    `https://ligasos-laravel.ligasos.co/api/countries/${id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setCountries(countries.filter((c) => c.id !== id));
                MySwal.fire({
                    icon: "success",
                    title: "Deleted",
                    text: "Country deleted successfully!",
                    confirmButtonColor: "#B39359",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (err) {
                MySwal.fire({
                    icon: "error",
                    title: "Error",
                    text: err.response?.data?.message || "Failed to delete country.",
                    confirmButtonColor: "#B39359",
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <section className="p-8 min-h-screen">
            <div className="">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-4xl font-extrabold text-[#0B2C3D] tracking-tight">Countries</h2>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        disabled={isLoading}
                        className="flex items-center gap-2 bg-[#B39359] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#D4A373] transition-colors duration-300 disabled:opacity-50 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Country
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#B39359]"></div>
                    </div>
                ) : countries.length === 0 ? (
                    <p className="text-center text-gray-500 text-lg font-medium">No countries found.</p>
                ) : (
                    <div className="overflow-x-auto bg-white rounded-xl shadow-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#0B2C3D] sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Image</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">English Name</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Arabic Name</th>
                                    <th className="px-6 py-4 text-right text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {countries.map((country, index) => (
                                    <tr
                                        key={country.id}
                                        className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                                    >
                                        <td className="px-6 py-4">
                                            {country.image ? (
                                                <img
                                                    src={`https://ligasos-laravel.ligasos.co/storage/app/public/${country.image}`}
                                                    alt={country.name_en}
                                                    className="w-12 h-12 object-cover border-2 border-[#B39359]"
                                                    onError={(e) => (e.target.src = "https://via.placeholder.com/40")}
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-medium">
                                                    No Image
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{country.name_en || "N/A"}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-[#0B2C3D]">{country.name_ar || "N/A"}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setEditCountry(country);
                                                        setIsEditModalOpen(true);
                                                    }}
                                                    className="p-2 text-[#0B2C3D] hover:bg-[#0B2C3D] hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <PencilIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteCountry(country.id)}
                                                    className="p-2 text-red-600 hover:bg-red-600 hover:text-white rounded-full transition-colors duration-300"
                                                    disabled={isLoading}
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modals */}
            <CountryModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddCountry}
                isEdit={false}
            />
            <CountryModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setEditCountry(null);
                }}
                onSubmit={handleEditCountry}
                initialData={editCountry}
                isEdit={true}
            />
        </section>
    );
}